/**
 * @component SignTypeSelectorDialog
 * @title 전자서명 수단 선택 팝업
 * @description
 *
 *  PUBLISH ID [
 *  - COMSIG000-10FP : 전자서명 수단 선택
 *  - COMSIG000-11FP : 전자서명 인증서 미존재
 * ]
 * @properties
 *  Props {
 *  }
 * @linked_menu
 * @author 2034263
 * @history
 *   - [2026-03-04] 초기 작성
 */
import { Trans, useTranslation } from 'react-i18next';

import {
  AccordionUnder,
  BulletList,
  BulletListItem,
  DialogWrapper,
  Empty,
  IconButtonToggle,
  InfoCard,
} from 'scbk-ui-web';

import { Channel, useChannelContext, useMenuRouter } from 'scbk-services';
import { SignVerifyType, usePageMove, useSession } from 'scbk-shared-module';

import { useSign } from '../hooks';

export const SignTypeSelectorDialog: React.FC<ReturnType<typeof useSign>> = ({
  signPopup,
  onCancelSign,
  selectSignType,
}) => {
  const { t } = useTranslation();
  const { channel } = useChannelContext();
  const { goMenu } = useMenuRouter();

  const { goMain } = usePageMove();
  const { getUserInfo, isLogin } = useSession();
  const { certInfo } = getUserInfo();

  // 모뱅환경 로그인인 경우 공동 금융 모두 없는 경우
  if (
    channel === Channel.MB && // 모바일뱅킹이면서
    isLogin() && // 로그인 상태면서
    certInfo?.finCertExist !== 'Y' &&
    certInfo?.jointCertExist !== 'Y' // 공동/금융 인증서를 둘 다 보유하지 않은 경우
  ) {
    return (
      <DialogWrapper
        title={t('Sign.020', '전자서명')}
        isFull
        open={signPopup.isOpen}
        setOpen={signPopup.setOpen}
        onClose={() => {
          onCancelSign();
        }}
        footer={[
          {
            type: 'primary',
            title: t('Sign.014', '확인'),
            hasClose: true,
            handler: () => {
              goMain();
            },
          },
        ]}
      >
        <Empty text={t('Sign.022', '보유한 인증서가 없어요')} />

        <InfoCard
          text={
            <Trans
              t={t}
              i18nKey="Sign.024"
              components={[<span className="block md:inline md:mr-1"></span>]}
            >
              <span className="block md:inline md:mr-1">인증서를 발급받고</span>
              편리하게 서비스를 이용하세요.
            </Trans>
          }
          className="mt-5"
          onClick={() => {
            goMenu('CRTHOM000', { historyOptions: { clearHistory: true } }); // 인증센터
          }}
        />

        <AccordionUnder openTitle={t('Sign.025', '알아두세요')}>
          <BulletList type="circle">
            <BulletListItem>
              {t(
                'Sign.026',
                '다른 은행에서 발급받은 금융인증서 또는 공동인증서가 있다면 타기관 인증서 등록 후 이용해 주세요.',
              )}
            </BulletListItem>
            <BulletListItem>
              {t(
                'Sign.027',
                'PC나 다른 스마트폰에 저장된 공동인증서가 있으면 인증서를 가져와 사용할 수 있어요.',
              )}
            </BulletListItem>
          </BulletList>
        </AccordionUnder>
      </DialogWrapper>
    );
  } else {
    return (
      <DialogWrapper
        title={t('Sign.020', '전자서명')}
        isFull
        open={signPopup.isOpen}
        setOpen={signPopup.setOpen}
        onClose={() => {
          onCancelSign();
        }}
      >
        <h2 className="page-heading-title mb-8">
          <Trans
            t={t}
            i18nKey="Sign.028"
            components={[<span className="block md:inline md:mr-1"></span>]}
          >
            <span className="block md:inline md:mr-1">전자서명을 위한</span>
            인증서를 선택해 주세요
          </Trans>
        </h2>

        <div className="mobile-id-group">
          <div className="flex gap-2">
            <IconButtonToggle
              items={[
                {
                  id: SignVerifyType.FIN_CERT_PIN,
                  icon: 'ico-shield-check',
                  title: t('Sign.029', '금융인증서'),
                },
                {
                  id: SignVerifyType.JOINT_CERT,
                  icon: 'ico-user-certificate',
                  title: t('Sign.030', '공동인증서'),
                },
              ]}
              onChange={(id) => {
                if (id === SignVerifyType.FIN_CERT_PIN) {
                  selectSignType(SignVerifyType.FIN_CERT_PIN);
                } else {
                  selectSignType(SignVerifyType.JOINT_CERT);
                }
              }}
            />
          </div>
        </div>
      </DialogWrapper>
    );
  }
};
